import React, { createContext, useContext, useEffect, useState } from "react";
import alertBus from "../../components/alert/alertBus";
import { MAX_ALERTS_COUNT, MAX_ALERTS_HISTORY_COUNT } from "../../constants";
import { v4 as uuidv4 } from "uuid";

type AlertVariant = "default" | "success" | "info" | "danger" | "warning" | "error" | "exception"

export interface AlertItem {
    id: string;
    variant: AlertVariant;
    title: string;
    instance?: string;
    detail?: string;
    statusCode?: number;
    timestamp: number;
    read?: boolean;
}

interface AlertsContextProps {
    alerts: AlertItem[];
    addAlert: (alert: Omit<AlertItem, "timestamp">) => void;
    removeAlert: (id: string) => void;
    clearAlerts: () => void;
    history: AlertItem[];
    clearHistory: () => void;
    removeFromHistory: (id: string) => void;
    markAsRead: (id: string) => void;
    markAsUnread?: (id: string) => void;
}

export const AlertsContext = createContext<AlertsContextProps | undefined>(undefined);

export const AlertsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [alerts, setAlerts] = useState<AlertItem[]>([]);      // массив сообщений из контекста, за сессию
    
    const [history, setHistory] = useState<AlertItem[]>(() => { // из localStorage за всю историю
        const saved = localStorage.getItem("alertsHistory");
        return saved ? JSON.parse(saved) : [];
    });
    

    const HISTORY_EXCLUDE: { variant: AlertVariant; title: string }[] = [
        { variant: "success", title: "Сохранено " },
        // сюда можно добавить и другие "ненужные" сообщения
    ];

    const addAlert = (alert: Omit<AlertItem, "timestamp">) => {
       
        // проверяем, нужно ли исключить
        const isExcluded = HISTORY_EXCLUDE.some(
            (rule) => rule.variant === alert.variant && rule.title === alert.title
        );
    
        if (isExcluded) {
            // просто показываем алерт, но в историю не пишем
            const newAlert: AlertItem = {
                ...alert,
                id: alert.id,
                timestamp: Date.now(),
            };
            setAlerts(prev => [newAlert, ...prev].slice(0, MAX_ALERTS_COUNT));
            return;
        }
    
        // обычное добавление
        const newAlert: AlertItem = {
            ...alert,
            id: alert.id,            
            timestamp: Date.now(),
        };
        
        setAlerts(prev => [newAlert, ...prev].slice(0, MAX_ALERTS_COUNT));        
        setHistory(prev => [newAlert, ...prev].slice(0, MAX_ALERTS_HISTORY_COUNT));
    };

    useEffect(() => {
        localStorage.setItem("alertsHistory", JSON.stringify(history));
    }, [history]);

    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<Omit<AlertItem, "timestamp">>; // кастомное событие для добавления алерта
            addAlert(customEvent.detail); // добавляем новый алерт из события
        };

        alertBus.addEventListener("addAlert", handler); // подписка на событие добавления алерта
        return () => {
            alertBus.removeEventListener("addAlert", handler); // отписка от события при размонтировании компонента
        };
    }, []);

    const removeAlert = (id: string) => {
        setAlerts(prev =>
            prev.filter(alert =>
                alert.id !== id
            )
        );
    };

    const clearAlerts = () => {
        setAlerts([]);
    };

    const markAsRead = (id: string) => {
        setHistory(prev =>
            prev.map(alert => 
                alert.id === id ? { ...alert, read: true } : alert
            )
        );
    };

    const markAsUnread = (id: string) => {
        setHistory(prev =>
            prev.map(alert =>
                alert.id === id ? { ...alert, read: false } : alert
            )
        );
    };
    
    const clearHistory = () => setHistory([]);

    const removeFromHistory = (id: string) => {
        setHistory(prev => prev.filter(alert => alert.id !== id));
    };

    return (
        <AlertsContext.Provider value={{ alerts, addAlert, removeAlert, clearAlerts, history, clearHistory, removeFromHistory, markAsRead, markAsUnread }}>
            {children}
        </AlertsContext.Provider>
    );
};

export const useAlerts = () => {
    const ctx = useContext(AlertsContext);
    if (!ctx) throw new Error("useAlerts must be used inside AlertsProvider");
    return ctx;
};